import React, { ButtonHTMLAttributes, ForwardedRef, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'dark';
  size?: 'sm' | 'md' | 'lg' | 'full';
  disabled?: boolean;
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((
  {
    children,
    variant = 'primary',
    size = 'md',
    disabled = false,
    loading = false,
    ...props
  }, ref: ForwardedRef<HTMLButtonElement>) => {
  const baseStyle = 'inline-block rounded-lg focus:outline-none focus:ring-0';

  const variants = {
    primary: 'bg-dark-100 text-white border border-gray-50 hover:bg-white hover:text-dark-100',
    secondary: 'bg-gray-5 text-dark-50 border border-gray-5 hover:border-gray-60 hover:bg-dark-100 hover:text-white',
    dark: 'bg-dark-200 border border-dark-200 text-white hover:bg-white hover:text-dark-200',
  };

  const sizes = {
    sm: 'px-4 py-1 text-base',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
    full: 'px-6 w-full py-2 text-base',
  };

  const classes = `
    ${baseStyle} 
    ${variants[variant]} 
    ${sizes[size]} 
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}
  `;

  return (
    <button
      className={classes}
      disabled={disabled}
      ref={ref}
      {...props}
    >
      {loading ? (
        <span className="flex items-center justify-center">
          <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V4a10 10 0 00-10 10h2zm6 6a8 8 0 01-8-8H0a10 10 0 0010 10v-2zm6-6a8 8 0 01-8 8v2a10 10 0 0010-10h-2zm6-6a8 8 0 01-8 8v2a10 10 0 0010-10h-2z"></path>
          </svg>
          Loading...
        </span>
      ) : children}
    </button>
  );
});

Button.displayName = 'Button';
export default Button;
